// src/components/legal/AcceptTerms.jsx
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldAlert, CheckCircle, ArrowRight } from 'lucide-react';
import { PALETTE, THEME_CLASSES } from '../../lib/theme.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { CONFIG } from '../../lib/config.js';

export default function AcceptTerms({ isDark }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loginData = location.state?.loginData;

    if (!loginData) {
        navigate('/login');
        return null;
    }

    const handleAccept = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${CONFIG.API_BASE_URL}/tenant/user/tos-accept`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${loginData.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ version: 'v1.0' })
            });

            if (!response.ok) throw new Error("Failed to record acceptance");

            login(loginData.token, loginData.tenant_id, loginData.user?.email, loginData.user, 'active');
            navigate('/');

        } catch (err) {
            console.error(err);
            setLoading(false);
            setError("There was an error recording your acceptance. Please try again.");
        }
    };

    return (
        <div className={`min-h-screen w-full flex items-center justify-center p-6 ${THEME_CLASSES.bgMain(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`} style={{ fontFamily: PALETTE.font }}>
            <div className={`max-w-xl w-full backdrop-blur-2xl rounded-[32px] p-8 md:p-12 shadow-2xl border ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}>

                <div className="flex flex-col items-center text-center mb-8">
                    <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-6">
                        <ShieldAlert size={32} style={{ color: PALETTE.blue }} />
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight mb-3">Updated Legal Terms</h1>
                    <p className={`text-sm ${THEME_CLASSES.textSecondary(isDark)}`}>
                        Before accessing your workspace, please review and accept our required compliance documentation.
                    </p>
                </div>

                <div className={`p-6 rounded-xl border mb-8 ${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}>
                    <ul className="space-y-4 text-sm font-medium">
                        <li className="flex items-center space-x-3">
                            <CheckCircle size={18} style={{ color: PALETTE.blue }} />
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">Terms of Service</a>
                        </li>
                        <li className="flex items-center space-x-3">
                            <CheckCircle size={18} style={{ color: PALETTE.blue }} />
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">Privacy Policy</a>
                        </li>
                    </ul>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-500 rounded-xl flex items-start gap-3 animate-in fade-in">
                        <ShieldAlert size={20} className="shrink-0 mt-0.5" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                )}

                <button
                    onClick={handleAccept}
                    disabled={loading}
                    className="w-full h-12 rounded-2xl font-bold text-white flex items-center justify-center space-x-2 transition-all active:scale-95 disabled:opacity-70"
                    style={{ backgroundColor: PALETTE.blue }}
                >
                    <span>{loading ? "Recording..." : "I have read and agree"}</span>
                    {!loading && <ArrowRight size={18} />}
                </button>

                <button
                    onClick={() => navigate('/login')}
                    disabled={loading}
                    className={`w-full mt-4 text-sm font-medium hover:underline ${THEME_CLASSES.textSecondary(isDark)} disabled:opacity-50`}
                >
                    Cancel and return to login
                </button>
            </div>
        </div>
    );
}